import { type FC, useCallback, useEffect, useState } from "react";

import { generateJiraLinkCode } from "../../api/issueIntegrations";
import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { CopyButton } from "../common/CopyButton";
import { Text } from "../common/Text";

interface JiraLinkCodeBlockProps {
  /** When true, a link code is generated automatically on mount. */
  autoGenerate?: boolean;
  /** Called with the generated code so the parent can react (e.g. start polling). */
  onCodeGenerated?: (code: string) => void;
}

/**
 * Generates and displays a Jira organisation link code.
 *
 * Shared between the onboarding wizard (JiraStep) and the settings
 * panel (JiraSetupPanel).
 */
export const JiraLinkCodeBlock: FC<JiraLinkCodeBlockProps> = ({
  autoGenerate = false,
  onCodeGenerated,
}) => {
  const [linkCode, setLinkCode] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const { code } = await generateJiraLinkCode();
      setLinkCode(code);
      onCodeGenerated?.(code);
    } catch (caughtError) {
      setError(
        caughtError instanceof Error
          ? caughtError.message
          : "Failed to generate link code.",
      );
    } finally {
      setGenerating(false);
    }
  }, [onCodeGenerated]);

  useEffect(() => {
    if (autoGenerate) {
      void handleGenerate();
    }
  }, [autoGenerate, handleGenerate]);

  return (
    <div className="rounded-none border border-slate-300 bg-slate-50/60 p-3 dark:border-slate-700 dark:bg-slate-900/40">
      <Text variant="label" as="h4" className="mb-1">
        Jira Link Code
      </Text>
      <Text as="p" className="mb-2">
        Generate a code and paste it into the Arti admin page in Jira (Settings
        &rarr; Apps &rarr; Arti Integration Settings) to link this organization.
      </Text>

      {error && (
        <Alert variant="error">
          <span className="text-xs">{error}</span>
        </Alert>
      )}

      {linkCode ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded-none border border-slate-300 bg-white px-2 py-1.5 font-mono text-xs text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-950 dark:text-white">
              {linkCode}
            </code>
            <CopyButton text={linkCode} />
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400">
            This code expires in 10 minutes.
          </p>
        </div>
      ) : (
        <Button
          type="button"
          onClick={() => void handleGenerate()}
          loading={generating}
          loadingText="Generating..."
        >
          Generate Link Code
        </Button>
      )}
    </div>
  );
};
